import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  ListSubheader,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { isNil } from "lodash";

import { displayFlex } from "../../styles/mui/styles/display";
import { FormInputProps } from "./FormInputProps";
import { GenderInputUI, OddEvenDropdownStyle } from "./styles/style";
import colors from "../../styles/colors/colors";
import fontWeight from "../../styles/mui/fontWeight";
import { getValue } from "../../utils";
import { DropdownOption } from "./constant/FormComponents";
import { SearchBox } from "components/styles/styles";

export const FormInputDropdown: React.FC<FormInputProps> = ({
  control,
  disabled,
  helper,
  label,
  list,
  name,
  onChangeHandler,
  mandatorySymbol,
  readOnly,
  InputStyle,
  oddEvenStyle,
  searchable = false,
  disableControlledOpen = false,
  disableMenuAutoHide = false,
  menuProps,
}) => {
  const selectRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [hideMenu, setHideMenu] = useState(false);
  const [searchText, setSearchText] = useState("");

  const checkInitialData: any = (data: any) =>
    Array.isArray(data) && data.length > 0 && data[0].label !== "";

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setHideMenu(!entry.isIntersecting);
      },
      {
        threshold: 0,
      }
    );

    if (selectRef.current) {
      observer.observe(selectRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const selectOpenProps = disableControlledOpen ? {} : {
    open,
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
  };

  const filteredOptions = useMemo(() => {
    if (!checkInitialData(list)) return [];

    if (!searchable || !searchText.trim()) return list;

    return (
      list &&
      list.filter((option) =>
        option.label?.toLowerCase().includes(searchText.toLowerCase())
      )
    );
  }, [list, searchText, searchable]);

  const generateSingleOptions = (options: DropdownOption[]) => {
    if (!options.length) {
      return <MenuItem disabled>No records found</MenuItem>;
    }

    return options.map((option, index) => (
      <MenuItem
        key={`${option.value}-${option.label}`}
        value={option.value}
        disabled={option.disabled || false}
        sx={oddEvenStyle ? OddEvenDropdownStyle(index) : { textWrap: "auto" }}
        data-testid={`${option.value}-${option.label}`}
      >
        {option.label}
      </MenuItem>
    ));
  };

  if (!list) return null;

  return (
    <FormControl variant="standard" size="small" sx={GenderInputUI}>
      {label && (
        <InputLabel
          required={helper?.required?.value ? helper.required.value : false}
          id={`${name}-label`}
          shrink
          sx={displayFlex}
        >
          <Box sx={displayFlex}>
            <Typography
              variant="h5"
              fontWeight={fontWeight.Weight[3]}
              color={colors.fonts[2]}
              lineHeight={"0.95rem"}
            >
              {label}
            </Typography>

            {!getValue(helper, "required.value", "") && mandatorySymbol && (
              <Typography
                variant="h3"
                fontWeight={fontWeight.Weight[3]}
                color={colors.red[100]}
                pl={"0.4rem"}
              >
                *
              </Typography>
            )}
          </Box>
        </InputLabel>
      )}

      <Controller
        defaultValue=""
        control={control}
        name={name}
        rules={helper}
        render={({
          field: { onChange, value, ref, name },
          fieldState: { error },
        }) => (
          <>
            <Select
              required={helper?.required?.value}
              ref={selectRef}
              fullWidth
              displayEmpty
              disabled={disabled}
              {...selectOpenProps}
              readOnly={readOnly}
              inputRef={ref}
              name={name}
              labelId={`${name}-label`}
              id={`${name}-select`}
              value={!isNil(value) && value !== 0 ? value : ""}
              error={!!error}
              sx={InputStyle}
              data-testid={name}
              inputProps={{
                "data-testid": `${name}-test`,
              }}
              onChange={(e) => {
                onChangeHandler?.(e);
                onChange(e);
                setSearchText("");
              }}
              MenuProps={{
                ...menuProps,
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                onClose: () => {
                  setOpen(false);
                  setSearchText("");
                  menuProps?.onClose?.();
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                disableScrollLock: true,
                slotProps: {
                  ...(menuProps?.slotProps || {}),
                  paper: {
                    ...(menuProps?.slotProps?.paper || {}),
                    style: {
                      maxHeight: 300,
                      display:
                        disableMenuAutoHide || !hideMenu ? "block" : "none",
                      ...(menuProps?.slotProps?.paper?.style || {}),
                    },
                  },
                },
              }}
            >
              {!searchable && (
                <MenuItem disabled value="">
                  Choose
                </MenuItem>
              )}

              {searchable && (
                <ListSubheader
                  sx={SearchBox}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    placeholder="Search"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 32,
                      },
                    }}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </ListSubheader>
              )}

              {generateSingleOptions(filteredOptions as DropdownOption[])}
            </Select>

            {helper?.required?.value && (
              <FormHelperText>{error?.message}</FormHelperText>
            )}
          </>
        )}
      />
    </FormControl>
  );
};
